import {
  EnvironmentProviders,
  Provider,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {
  provideRouter,
  withExperimentalAutoCleanupInjectors,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { IconRegistryService } from '@cbcore/index';
import { provideCbUi } from '@cbui/providers';
import { provideIonicAngular } from '@ionic/angular';
import { Store, provideState } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import * as Sentry from '@sentry/angular';

import { AppState } from '@cbcore/NGRX/core.state';
import { Config } from '@cbcore/services/config/config.service';
import { CommunicationHandlerService } from '@cbcore/services/utils/communication-handler/communication-handler.service';
import { API_DATA_TOKEN } from '@cbcore/client/image';
import { provideCbfeCore } from '@cbcore/cbfe-core.config';
import { routes } from './app.routes';
import { CustomPreloadingStrategyService } from './custom-preloading-strategy';
import { MerchantConfig } from './config/config.service';
import { MerchantService } from './services/merchant';
import { MerchantEffects } from './config/NGRX/merchant/merchant.effects';
import { merchantReducer } from './config/NGRX/merchant/merchant.reducer';
import { AppConfig } from './app.config.service';
import { ExtendedCommunicationHandler } from './services/extended-communication-handler.service';
import { provideTranslation } from './providers/translation.providers';
import { provideEnvironmentTokens } from './providers/environment.providers';

export function MerchantConfigFactory(store: Store<AppState>): any {
  return new MerchantConfig(store);
}

export const appProviders: Array<Provider | EnvironmentProviders> = [
  provideRouter(
    routes,
    withPreloading(CustomPreloadingStrategyService),
    withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
    withRouterConfig({ paramsInheritanceStrategy: 'always' }),
    withExperimentalAutoCleanupInjectors(),
  ),

  provideCbfeCore(),
  provideState('merchant', merchantReducer),
  provideEffects(MerchantEffects),

  ...provideTranslation(),
  ...provideEnvironmentTokens(),

  {
    provide: Config,
    useFactory: MerchantConfigFactory,
    deps: [Store],
  },
  // Duplicate API_DATA_TOKEN registration preserved as-is from the original
  // AppModule — the useFactory entry below wins (last provider registered).
  {
    provide: API_DATA_TOKEN,
    useValue: {
      total_items: 20,
      prefix: 'Avatar_',
      suffix: '.png',
      default_image: 'default',
    },
  },
  MerchantService,
  {
    provide: CommunicationHandlerService,
    useClass: ExtendedCommunicationHandler,
  },
  {
    provide: API_DATA_TOKEN,
    useFactory: () => {
      return {
        total_items: 20,
        prefix: 'Avatar_',
        suffix: '.png',
        default_image: 'default',
      };
    },
  },
  AppConfig,
  provideCbUi(),
  provideIonicAngular(),

  provideAppInitializer(() => {
    const appConfigService = inject(AppConfig);
    return appConfigService.loadInitialData();
  }),
  provideAppInitializer(() => {
    const iconService = inject(IconRegistryService);
    iconService.init();
  }),
  provideAppInitializer(() => {
    inject(Sentry.TraceService);
    return Promise.resolve();
  }),
];
